import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Spinner } from "reactstrap";
import { Modal, Select, Tag } from "antd";
import { Button, Icon } from "../../../../components/Component";
import { deploymentNormApis } from "../../../../api/MachineryEquipment";
import { projectsApis } from "../../../../api/projects";

// Duplicate ORG-MASTER deployment norms into a real project — the enabler for
// apply-to-task, whose applicability rule is project-only (norm.projectId ===
// task.projectId; org-master norms are never directly applicable). Per norm
// the server creates a project-scoped header + ONE version seeded from the
// matrix in force now. Codes already Active in the target and headers with no
// in-force version are skipped with a reason, never an error.
//
// The target select deliberately EXCLUDES the org-master entry — duplication
// targets a real project (options are built from the same project list
// ProjectScopeSelect uses, minus the org row).

interface OrgNorm {
  deploymentNormId: number;
  normCode: string;
  description?: string | null;
  equipmentCount?: number;
  hasVersion?: boolean;
  status?: string;
}

interface DuplicateResult {
  insertedCount: number;
  skippedCount: number;
  message?: string;
  skipped: any[];
}

type Stage = "form" | "done";

/** skipped[] items may arrive as plain strings or {normCode, error|reason}. */
const skipLabel = (s: any): string => {
  if (s == null) return "";
  if (typeof s === "string") return s;
  const code = s.normCode || (s.deploymentNormId ? `#${s.deploymentNormId}` : "");
  const why = s.error || s.reason || s.message || "";
  return [code, why].filter(Boolean).join(" — ");
};

const DuplicateDeploymentNormsModal = ({
  open,
  onClose,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  /** Fired on Done with the target projectId, so the list can refresh when it is showing that project. */
  onSuccess?: (targetProjectId: number) => void;
}) => {
  const [stage, setStage] = useState<Stage>("form");
  const [projects, setProjects] = useState<{ value: number; label: string }[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [norms, setNorms] = useState<OrgNorm[]>([]);
  const [loadingNorms, setLoadingNorms] = useState(false);
  const [targetProjectId, setTargetProjectId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [projectError, setProjectError] = useState("");
  const [itemsError, setItemsError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<DuplicateResult | null>(null);

  const resetAll = useCallback(() => {
    setStage("form");
    setTargetProjectId(null);
    setProjectError("");
    setItemsError("");
    setSubmitting(false);
    setError("");
    setResult(null);
  }, []);

  // Real projects only — same fetch ProjectScopeSelect uses, minus the org row.
  const loadProjects = useCallback(async () => {
    try {
      setLoadingProjects(true);
      const res = await projectsApis.getProjectList({
        page: 1,
        pageSize: 100,
        searchQuery: null,
        direction: "P",
        sortOrder: "DESC",
        pageDiff: 0,
        paginationId: null,
        projectStatus: "Active",
      });
      const list: any[] = res?.data?.list || [];
      setProjects(
        list.map((project: any) => ({
          value: Number(project?.projectId),
          label: project?.projectTitle || `Project ${project?.projectId}`,
        })),
      );
    } catch {
      setError("Failed to load the project list");
    } finally {
      setLoadingProjects(false);
    }
  }, []);

  // Active org-master norms, ALL preselected — deselect to narrow the copy.
  const loadNorms = useCallback(async () => {
    try {
      setLoadingNorms(true);
      const res = await deploymentNormApis.getList({
        page: 1,
        pageSize: 200,
        direction: "P",
        sortOrder: "DESC",
        pageDiff: 0,
        paginationId: null,
        searchQuery: null,
        projectId: 0,
      });
      if (res?.status === true) {
        const list: any[] = res?.data?.list || [];
        const active = list
          .filter((n: any) => n?.status === "Active")
          .map((n: any) => ({
            deploymentNormId: Number(n.deploymentNormId),
            normCode: n.normCode,
            description: n.description,
            equipmentCount: Number(n.equipmentCount) || 0,
            hasVersion: !!n.hasVersion,
            status: n.status,
          }));
        setNorms(active);
        setSelectedIds(active.map((n) => n.deploymentNormId));
      } else {
        setError(res?.message || "Failed to load the org-master norms");
      }
    } catch {
      setError("Failed to load the org-master norms");
    } finally {
      setLoadingNorms(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      resetAll();
      loadProjects();
      loadNorms();
    }
  }, [open, resetAll, loadProjects, loadNorms]);

  const normMap = useMemo(() => {
    const m = new Map<number, OrgNorm>();
    norms.forEach((n) => m.set(n.deploymentNormId, n));
    return m;
  }, [norms]);

  const normOptions = useMemo(
    () =>
      norms.map((n) => ({
        value: n.deploymentNormId,
        label: n.description ? `${n.normCode} — ${n.description}` : n.normCode,
      })),
    [norms],
  );

  // Rich option row: «code — description» + no-version tag + equipment count.
  const renderOption = useCallback(
    (option: any) => {
      const n = normMap.get(Number(option.value));
      if (!n) return option.label;
      return (
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {option.label}
          </span>
          {!n.hasVersion && (
            <Tag color="orange" style={{ margin: 0, fontSize: 11, lineHeight: "18px" }}>
              no version — will skip
            </Tag>
          )}
          <span style={{ color: "#8094ae", fontSize: 12, whiteSpace: "nowrap" }}>
            {n.equipmentCount} equipment
          </span>
        </div>
      );
    },
    [normMap],
  );

  const validate = () => {
    let ok = true;
    if (!targetProjectId) {
      setProjectError("Please pick the target project");
      ok = false;
    } else {
      setProjectError("");
    }
    if (!selectedIds.length) {
      setItemsError("Please select at least one norm to duplicate");
      ok = false;
    } else {
      setItemsError("");
    }
    return ok;
  };

  const handleDuplicate = async () => {
    if (!validate()) return;
    setError("");
    try {
      setSubmitting(true);
      // Ids are always sent explicitly (even when everything is selected) so
      // what was on screen is exactly what the server copies.
      const res = await deploymentNormApis.duplicateToProject({
        projectId: Number(targetProjectId),
        deploymentNormIds: selectedIds.map((id) => Number(id)),
      });

      if (res?.status === true) {
        const data = res?.data || {};
        setResult({
          insertedCount: Number(data.insertedCount) || 0,
          skippedCount: Number(data.skippedCount) || 0,
          message: data.message,
          skipped: Array.isArray(data.skipped) ? data.skipped : [],
        });
        setStage("done");
        return;
      }

      const rawMessage = res?.data?.message ?? res?.message;
      const message = Array.isArray(rawMessage)
        ? rawMessage.join("; ")
        : rawMessage || "Failed to duplicate the equipment norms. Please try again.";
      setError(message);
    } catch (err: any) {
      setError(
        err?.message || "Something went wrong while duplicating the equipment norms",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    if (targetProjectId != null) onSuccess?.(Number(targetProjectId));
    onClose();
  };

  const noNorms = !loadingNorms && norms.length === 0;
  const targetLabel =
    projects.find((p) => p.value === targetProjectId)?.label || "";

  return (
    <Modal
      title="Duplicate Equipment Norms to Project"
      open={open}
      footer={null}
      onCancel={stage === "done" ? handleDone : onClose}
      maskClosable={false}
      width={640}
    >
      {stage === "form" && (
        <div>
          <p className="mb-3">
            Org-master norms are not directly applicable to tasks — applying
            needs the norm in the task's <em>own</em> project. Duplicating
            copies each selected norm into the target project as a new
            project-scoped norm with one version seeded from the matrix in
            force now. Codes already Active in the target and norms with no
            version are skipped, not overwritten.
          </p>

          <div className="form-group">
            <label className="form-label">
              Target Project <span style={{ color: "red" }}>*</span>
            </label>
            <Select
              style={{ width: "100%" }}
              placeholder="Select the project to copy into"
              showSearch
              optionFilterProp="label"
              loading={loadingProjects}
              options={projects}
              value={targetProjectId ?? undefined}
              onChange={(value) => {
                setTargetProjectId(Number(value));
                setProjectError("");
              }}
              disabled={submitting}
              status={projectError ? "error" : undefined}
            />
            {projectError && (
              <span className="invalid" style={{ color: "#e85347", fontSize: 12 }}>
                {projectError}
              </span>
            )}
          </div>

          <div className="form-group mt-2">
            <label className="form-label">
              Norms to Duplicate <span style={{ color: "red" }}>*</span>{" "}
              <span className="text-soft" style={{ fontWeight: 400 }}>
                (all Active org-master norms preselected)
              </span>
            </label>
            <Select
              style={{ width: "100%" }}
              mode="multiple"
              placeholder="Select the norms to copy"
              showSearch
              optionFilterProp="label"
              loading={loadingNorms}
              options={normOptions as any}
              optionRender={renderOption}
              value={selectedIds}
              onChange={(value) => {
                setSelectedIds(value as number[]);
                setItemsError("");
              }}
              disabled={submitting || noNorms}
              status={itemsError ? "error" : undefined}
              maxTagCount="responsive"
            />
            {itemsError && (
              <span className="invalid" style={{ color: "#e85347", fontSize: 12 }}>
                {itemsError}
              </span>
            )}
            {noNorms && (
              <Alert
                color="light"
                className="py-2 mt-2 mb-0"
                isOpen
                style={{ textAlign: "left", fontSize: "13px" }}
              >
                No Active org-master equipment norms to duplicate — add them
                on the <strong>Organization (Master)</strong> scope first.
              </Alert>
            )}
          </div>

          <Alert
            isOpen={!!error}
            color="danger"
            className="py-1 mt-3 mb-0"
            style={{ textAlign: "left", fontSize: "14px" }}
          >
            {error}
          </Alert>

          <div className="d-flex mt-4" style={{ gap: 12 }}>
            {submitting ? (
              <Button color="primary" type="button">
                <Spinner size="sm" color="light" />
              </Button>
            ) : (
              <Button color="primary" onClick={handleDuplicate} disabled={noNorms}>
                <Icon name="copy" />
                <span>Duplicate</span>
              </Button>
            )}
            {!submitting && (
              <Button
                color="primary"
                className="btn-dim btn btn-secondary"
                onClick={onClose}
              >
                Cancel
              </Button>
            )}
          </div>
        </div>
      )}

      {stage === "done" && result && (
        <div>
          <div className="text-center py-2">
            <Icon name="check-circle" style={{ fontSize: 42, color: "#1ee0ac" }} />
            <h6 className="mt-2">
              {result.insertedCount} norm(s) duplicated
              {targetLabel ? ` into ${targetLabel}` : ""}
            </h6>
            {result.message && (
              <div className="text-soft" style={{ fontSize: 13 }}>
                {result.message}
              </div>
            )}
          </div>
          {result.skippedCount > 0 && (
            <Alert
              color="warning"
              className="py-2 mt-2 mb-0"
              style={{ textAlign: "left", fontSize: "14px" }}
            >
              <strong>Skipped ({result.skippedCount})</strong>
              {result.skipped.length > 0 && (
                <ul className="mt-1 mb-0" style={{ paddingLeft: 18 }}>
                  {result.skipped.map((skip, index) => (
                    <li key={index}>{skipLabel(skip)}</li>
                  ))}
                </ul>
              )}
            </Alert>
          )}
          <div className="d-flex justify-content-center mt-3">
            <Button color="primary" onClick={handleDone}>
              Done
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default DuplicateDeploymentNormsModal;
